import React, { FC, useState } from 'react';
import { type HeadFC, Link, type PageProps, graphql } from 'gatsby';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { compact, map, take, uniqBy } from 'lodash';
import Layout from '@/components/Layout/Layout';
import Section from '@/components/Common/Section';
import TitleBodyGroup from '@/components/Common/TitleBodyGroup';
import Hero from '@/components/Common/Hero';
import { HeaderVariant } from '@/components/Layout/Header';
import { ProductCardGrid } from '@/components/Common/ProductList';
import { Lead } from '@/components/Common/Text';
import Card from '@/components/Common/Card';
import Slider from '@/components/Common/Slider';
import CollectionFeatures from '@/components/Common/CollectionFeatures';
import FAQs from '@/components/Common/FAQs';
import ProjectsSlider from '@/components/Common/ProjectsSlider';
import SEO from '@/components/Common/SEO';
import routes from '@/routes';
import Asset from '@/components/Common/Asset';
import Button, { ButtonVariant } from '@/components/Common/Button';
import { useWishlist } from '@/context/Wishlist';
import { Breakpoint, useBreakpointAbove } from '@/hooks/useBreakpoint';
import { useI18n, withI18nContext } from '@/context/I18n';
import {
  ProductBundleConfigurator,
  ProductConfigurator,
} from '@/components/Common/ProductConfigurator';
import ColorConfigurationPreview from '@/components/Common/ColorConfigurationPreview';
import ScrollableTimeline from '@/components/Common/ScrollableTimeline';
import TechnicalSpecs from '@/components/Common/TechnicalSpecs';
import Loading from '@/components/Common/Loading';
import AR from '@/components/Block/AR';
import ProductContextualNavigation from '@/components/Block/ContextualNavigation/Product';
import LightingModule from '@/components/Block/LightingModule';
import MakeItYours360 from '@/components/Block/MakeItYours360';
import Superfan from '@/components/Block/Superfan';
import TailoredNeeds from '@/components/Block/TailoredNeeds';
import IconFeatures, { Feature } from '@/components/Block/IconFeatures';
import { ConfiguredProduct } from '@/types/product';
import { safeParse } from '@/utils/misc';
import {
  configureDefaultProductBundle,
  isConfigurable,
  makeDefaultConfiguration,
} from '@/utils/configurator';
import { isBundle } from '@/utils/product';
import usePreloadAsset from '@/hooks/usePreloadAsset';
import useLocalAnchorNavigate from '@/hooks/useLocalAnchorNavigate';
import FeaturesCards from '@/components/Common/FeaturesCards';
import DiscoverCards from '@/components/Block/DiscoverCards';
import Modal from '@/components/Common/Modal';
import LogosGrid from '@/components/Block/LogosGrid';
import { ProductSlider } from '@/components/Common/ProductList/ProductSlider';
import * as styles from './Product.module.scss';

type PageContext = {
  locale: string;
  title: string;
  slug: string;
  collection?: string;
  categories?: string[];
};

const ProductPage: FC<PageProps<Queries.ProductPageQuery, PageContext>> = ({
  data,
  pageContext,
}) => {
  const { locale } = useI18n();
  const [isTechnicalDrawingModalOpen, setIsTechnicalDrawingModalOpen] = useState(false);
  const product = data.product;
  const productName = product?.name;
  const collectionModule = pageContext.collection
    ? data.collectionModule.nodes?.find((node) => node?.key === pageContext.collection)
    : null;
  const sku = product?.pim?.sku;
  const { add } = useWishlist();
  const { navigate } = useLocalAnchorNavigate();
  const goToConfigure = () => navigate('#configure');

  const technicalDrawings = (product?.technicalDrawing ?? []).filter(Boolean);
  const isBundleProduct = isBundle(product);
  const canConfigure = isConfigurable(product);
  const detailedFeatures = safeParse<Feature[]>(
    product?.detailedFeaturesSpecification?.features
  );

  const handleExpandTechnicalDrawing = () => {
    setIsTechnicalDrawingModalOpen(true);
  };

  const isLargeViewport = useBreakpointAbove(Breakpoint.MobileLandscape);

  const relatedProducts = take(
    uniqBy(
      [
        ...data.relatedProductsByCollection.nodes,
        ...data.relatedProductsByCategory.nodes,
      ],
      'sku'
    ),
    9
  );
  const relatedProjects = compact(product?.projects);
  const defaultConfiguration = makeDefaultConfiguration(product!);
  const handleAddToWishlistClick = () => {
    add({
      ...defaultConfiguration,
      name: product?.name!,
    });
  };

  const { isLoading: isColorConfigVideoLoading } = usePreloadAsset(
    product?.colorConfigurationPreview?.video360CdnUrl
  );

  const isLargeDevice = useBreakpointAbove(Breakpoint.TabletPortrait);

  const makeItYours = (
    <>
      <div className={styles.makeItYoursIntro}>
        <h2>
          <Trans>Make it yours</Trans>
        </h2>
        <div className={styles.makeItYoursActions}>
          {canConfigure ? (
            <Button onClick={goToConfigure} variant={ButtonVariant.Ghost}>
              <Trans>Configure</Trans>
            </Button>
          ) : (
            <Button onClick={handleAddToWishlistClick} variant={ButtonVariant.Ghost}>
              <Trans>Add to estimate</Trans>
            </Button>
          )}

          <Button
            as={Link}
            to={routes.findYourDealer(locale)}
            variant={ButtonVariant.Ghost}
          >
            <Trans>Find your dealer</Trans>
          </Button>
        </div>
      </div>

      <div className={styles.makeItYoursContent}>
        <ColorConfigurationPreview
          finishes={map(product?.colorConfigurationPreview?.options, (option) => [
            option?.color?.hex!,
            option?.name!,
          ])}
          previews={map(
            product?.colorConfigurationPreview?.options,
            (option) => option?.asset!
          )}
        />
      </div>
    </>
  );

  return (
    <Layout
      headerProps={{
        variant: HeaderVariant.Transparent,
        isFloating: true,
        contextualNavigation: (
          <ProductContextualNavigation
            title={product?.name!}
            links={[
              {
                label: i18n._(t`Overview`),
                path: '#overview',
              },
              {
                label: i18n._(t`Technical specs`),
                path: '#technical-specs',
              },
            ]}
            actions={
              <Button variant={ButtonVariant.Primary} onClick={handleAddToWishlistClick}>
                {isLargeViewport ? (
                  <Trans>Add to estimate</Trans>
                ) : (
                  <Trans context="Add to estimate">Add</Trans>
                )}
              </Button>
            }
          />
        ),
      }}
    >
      <Section>
        <Hero image={<Asset asset={product?.hero} />}>
          <hgroup>
            {product?.collection?.slug ? (
              <Link to={routes.collection(locale, product?.collection?.slug)}>
                {product?.collection?.name}
              </Link>
            ) : null}
            <h1>{product?.name}</h1>
          </hgroup>
          <p>
            <Trans>Reference: {sku} | Designed by </Trans>{' '}
            {product?.designers?.map((designer, index) => (
              <Link key={index} to={routes.designer(locale, designer?.slug!)}>
                {designer?.name}
              </Link>
            ))}
          </p>
        </Hero>
      </Section>
      <Section id="overview">
        <Lead>{product?.lead}</Lead>
      </Section>

      {product?.intro ? (
        <Section>
          <TitleBodyGroup title={<h2>{product.intro.text?.title}</h2>}>
            {product.intro.text?.body}
          </TitleBodyGroup>

          {product.intro.cards?.length ? (
            <Slider className={styles.introCards}>
              {(product.intro.cards || []).filter(Boolean).map((card, index) => (
                <Card
                  key={index}
                  body={card!.body}
                  isInset
                  image={
                    <Asset
                      key={`asset-${index}`}
                      asset={card!.asset}
                      shouldUseCustomImage
                    />
                  }
                />
              ))}
            </Slider>
          ) : null}
        </Section>
      ) : null}

      {product?.colorConfigurationPreview ? (
        <Section className={styles.colorConfiguration}>
          {isLargeDevice ? (
            isColorConfigVideoLoading ? (
              <Loading />
            ) : (
              <ScrollableTimeline>
                <MakeItYours360
                  videoMp4={product?.colorConfigurationPreview?.video360?.video?.mp4Url}
                  videoWebm={product?.colorConfigurationPreview?.video360CdnUrl!}
                >
                  {makeItYours}
                </MakeItYours360>
              </ScrollableTimeline>
            )
          ) : (
            makeItYours
          )}
        </Section>
      ) : null}

      {canConfigure ? (
        <Section id="configure" title={<Trans>Configure your {productName}</Trans>}>
          {isBundleProduct ? (
            <ProductBundleConfigurator
              sku={product.sku}
              options={product.bundleOptions as any}
              defaultValue={configureDefaultProductBundle(product).options}
              shouldUseFallbackImage={!product?.pim?.configurator}
              fallbackImage={product.image}
              onDone={(options) => {
                add({
                  sku: product.sku!,
                  name: product.name!,
                  quantity: 1,
                  options,
                });
              }}
            />
          ) : (
            <ProductConfigurator
              product={{
                sku: product!.sku,
                name: product!.name!,
                label: product!.name!,
                isDefault: false,
                finishGroups: product!.finishGroups ?? [],
                position: 1,
                canChangeQuantity: false,
                pim: product!.pim!,
              }}
              defaultValue={(defaultConfiguration as ConfiguredProduct).characteristics}
              characteristics={product?.pim?.characteristics!}
              onDone={(characteristics) => {
                add({
                  sku: product?.sku!,
                  name: product?.name!,
                  quantity: 1,
                  characteristics,
                });
              }}
            />
          )}
        </Section>
      ) : null}

      {product?.features ? (
        <FeaturesCards
          content={product.features}
          isExpansible={!!product.expansibleFeatures}
        />
      ) : null}

      {collectionModule && collectionModule.blocks?.length ? (
        <Section>
          <CollectionFeatures
            name={collectionModule.name}
            lead={collectionModule.lead}
            features={collectionModule.blocks}
          />
        </Section>
      ) : null}

      {data.product?.includesLightingModule ? (
        <Section>
          <LightingModule />
        </Section>
      ) : null}

      {product?.generalFeatures ? (
        <Section>
          <TailoredNeeds
            title={data.product?.generalFeatures?.title!}
            features={(data.product?.generalFeatures?.blocks as any) || []}
          />
        </Section>
      ) : null}

      {product?.video ? (
        <Section bleeds>
          <Asset
            className={styles.video}
            asset={product.video}
            videoProps={{
              canListen: true,
              autoPlay: false,
              controls: true,
              loop: false,
            }}
          />
        </Section>
      ) : null}

      <Section id="technical-specs">
        <TitleBodyGroup
          title={
            <h2>
              <Trans>Technical specifications</Trans>
            </h2>
          }
        >
          <p>
            <Trans>
              The space where accessories meet advanced tech to craft a comfortable,
              silent, and efficient zone for your team.
            </Trans>
          </p>
        </TitleBodyGroup>

        <div className={styles.technicalSpecsContent}>
          <div className={styles.technicalSpecsDrawing}>
            {technicalDrawings?.length > 0 ? (
              <>
                {technicalDrawings?.length > 1 ? (
                  <Button
                    variant={ButtonVariant.Bare}
                    onClick={handleExpandTechnicalDrawing}
                  >
                    <Trans>+ View detailed plan</Trans>
                  </Button>
                ) : null}
                <Asset key={0} asset={technicalDrawings[0]} shouldUseCustomImage />
              </>
            ) : null}
          </div>

          <div>
            {detailedFeatures?.length ? (
              <IconFeatures
                className={styles.iconFeatures}
                features={take(detailedFeatures ?? [], 6)}
              />
            ) : null}
            <TechnicalSpecs
              sku={data.product?.sku}
              files={data.product?.files3d}
              specsheet={
                data.product?.specsheet
                  ? data.product.specsheet
                  : data.product?.pim?.specsheet
                    ? {
                        title: i18n._(t`Specsheet PDF`),
                        url: data.product?.pim?.specsheet,
                        filename: null,
                      }
                    : null
              }
            />
          </div>
        </div>
      </Section>

      {product?.faqs?.length ? (
        <Section>
          <FAQs
            title={
              <h2>
                <Trans>Get the FAQs</Trans>
              </h2>
            }
            items={
              product?.faqs?.map((faq) => ({
                title: faq?.question || '',
                children: <>{faq?.answer}</>,
              })) || []
            }
          />
        </Section>
      ) : null}

      {data.product?.includesArModule ? (
        <Section>
          <AR content={data?.ar?.content} url={product?.arUrl || data?.ar?.defaultUrl} />
        </Section>
      ) : null}

      {data.product?.includesSuperfanModule ? (
        <Section>
          <Superfan data={data?.superfan} />
        </Section>
      ) : null}

      {relatedProjects.length > 0 ? (
        <Section title={<Trans>Related Projects</Trans>}>
          <ProjectsSlider projects={relatedProjects} />
        </Section>
      ) : null}

      {relatedProducts.length > 0 ? (
        <Section title={<Trans>Related Products</Trans>}>
          <ProductSlider products={relatedProducts} />
        </Section>
      ) : null}

      {data.datoCmsPartnerLogosCarousel ? (
        <Section>
          <LogosGrid content={data.datoCmsPartnerLogosCarousel} />
        </Section>
      ) : null}

      <DiscoverCards />

      <Modal
        label={i18n._(t`Technical drawing`)}
        isOpen={isTechnicalDrawingModalOpen}
        onClose={() => setIsTechnicalDrawingModalOpen(false)}
      >
        <div className={styles.technicalDrawingModal}>
          <Asset asset={technicalDrawings[1]} shouldUseCustomImage />
        </div>
      </Modal>
    </Layout>
  );
};

export default ProductPage;

export const query = graphql`
  query ProductPage(
    $slug: String
    $locale: String!
    $collection: String
    $categories: [String]
  ) {
    product: datoCmsProduct(
      locale: $locale
      fallbackLocales: "en-GB"
      slug: { eq: $slug }
    ) {
      ...DatoCmsProductSummaryWithBundleOptions

      lead
      hero {
        ...DatoCmsAsset
      }

      dimensions: dimension
      specsheet {
        ...DatoCmsDownload
      }

      technicalDrawing {
        ...DatoCmsGalleryAsset
      }

      intro {
        ...DatoCmsCardGroup
        cards {
          asset {
            resizedImage: gatsbyImageData(layout: CONSTRAINED, height: 1200)
          }
        }
      }

      features {
        ...DatoCmsCardGroup
        cards {
          asset {
            resizedImage: gatsbyImageData(layout: CONSTRAINED, width: 600, height: 1200)
          }
        }
      }

      expansibleFeatures

      generalFeatures {
        title(locale: $locale)
        blocks(locale: $locale) {
          pretitle
          title
          body
          asset {
            url
          }
        }
      }

      video {
        ...DatoCmsAsset
      }

      detailedFeaturesSpecification {
        ...DatoCmsFeatureGroup
      }

      includesArModule
      arUrl
      includesLightingModule
      includesSuperfanModule

      faqs {
        answer
        question
      }

      files3d: pim {
        DWG
        MAX
        IES
        OBJ
        Revit
        SketchUp
      }

      pim {
        specsheet: Specsheet_PDF
      }

      finishGroups {
        name
        code
        finishes {
          code
          name
          position
          titleGroup
          image {
            childImageSharp {
              gatsbyImageData(width: 300, height: 300, layout: CONSTRAINED)
            }
          }
        }
      }

      colorConfigurationPreview {
        video360 {
          ...DatoCmsAsset
        }
        video360CdnUrl

        options {
          name
          asset {
            ...DatoCmsAsset
          }
          color {
            hex
          }
        }
      }

      projects {
        ...DatoCmsProjectSummary
      }

      seo {
        title
        description
      }
    }

    collectionModule: allDatoCmsCollectionModule(filter: { locales: { in: [$locale] } }) {
      nodes {
        id
        key
        name
        lead(locale: $locale)
        blocks(locale: $locale) {
          ...DatoCmsTextAssetGroup
        }
      }
    }

    ar: datoCmsAugmentedReality {
      content {
        ...DatoCmsTextAssetGroup
      }
      defaultUrl
    }

    superfan: datoCmsSuperfan {
      content {
        ...DatoCmsTextAssetGroup
      }
      qr {
        ...DatoCmsAsset
      }
      appStoreUrl
      googlePlayUrl
    }

    findYourDealer: datoCmsFindYourDealer {
      content {
        ...DatoCmsTextAssetGroup
      }
    }

    relatedProductsByCollection: allDatoCmsProduct(
      filter: {
        slug: { ne: $slug }
        collection: { slug: { eq: $collection } }
        pim: { status: { ne: "disabled" } }
      }
      limit: 9
    ) {
      nodes {
        ...DatoCmsProductSummary
      }
    }

    relatedProductsByCategory: allDatoCmsProduct(
      filter: {
        slug: { ne: $slug }
        pim: { status: { ne: "disabled" }, categories: { in: $categories } }
      }
      limit: 9
    ) {
      nodes {
        ...DatoCmsProductSummary
      }
    }

    datoCmsPartnerLogosCarousel(locale: $locale) {
      ...DatoCmsPartnerLogosCarousel
    }
  }
`;

export const Head: HeadFC<Queries.ProductPageQuery, PageContext> = withI18nContext(
  ({ data, pageContext }) => {
    const { title } = pageContext;
    const product = data.product;
    const { seo } = product || {};
    return (
      <SEO
        path={routes.product(pageContext.locale, product?.slug!)}
        attributes={{
          title: seo?.title || title,
          description: seo?.description ?? undefined,
        }}
      />
    );
  }
);
